import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauth, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Logout should always resolve on the client side even if the server call fails.
    }
    setUser(false);
  }, []);

  const updateCurrency = useCallback(async (currency) => {
    const { data } = await api.put("/settings", { currency });
    setUser((u) => ({ ...u, currency: data.currency }));
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, refresh, updateCurrency, formatApiErrorDetail }),
    [user, login, register, logout, refresh, updateCurrency],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
